import * as BaseSlider from "@radix-ui/react-slider";
import clsx from "clsx";
import { useId } from "react";
import * as sliderStyles from "./Slider.css";

export interface SliderProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  /**
   * Current value of the slider (controlled).
   */
  value: number;
  /**
   * Callback fired continuously while the value changes (during drag).
   */
  onChange?: (value: number) => void;
  /**
   * Callback fired once when the user commits a value (pointer/key release).
   * Use this to trigger expensive side effects instead of `onChange`.
   */
  onCommit?: (value: number) => void;
  /**
   * Minimum value. Defaults to 0.
   */
  min?: number;
  /**
   * Maximum value. Defaults to 100.
   */
  max?: number;
  /**
   * Step increment. Defaults to 1.
   */
  step?: number;
  /**
   * Label to display next to the slider.
   */
  label?: string;
  /**
   * If `true`, the slider will be disabled.
   */
  disabled?: boolean;
  /**
   * If provided, shows a bubble above the thumb with the formatted value
   * while hovering or dragging.
   */
  formatValue?: (value: number) => string;
}

export function Slider({
  value,
  onChange,
  onCommit,
  min = 0,
  max = 100,
  step = 1,
  label,
  disabled,
  formatValue,
  className,
  ...rest
}: SliderProps) {
  const id = useId();

  return (
    <div className={clsx(sliderStyles.sliderContainer, className)} {...rest}>
      {label !== undefined && (
        <label className={sliderStyles.sliderLabel} htmlFor={id}>
          {label}
        </label>
      )}
      <div className={sliderStyles.sliderTrackBackground}>
        <BaseSlider.Root
          id={id}
          className={sliderStyles.sliderRoot}
          value={[value]}
          onValueChange={(next) => onChange?.(next[0])}
          onValueCommit={(next) => onCommit?.(next[0])}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        >
          <BaseSlider.Track className={sliderStyles.sliderTrack}>
            <BaseSlider.Range className={sliderStyles.sliderRange} />
          </BaseSlider.Track>
          <BaseSlider.Thumb
            className={sliderStyles.sliderThumb}
            aria-label={label ?? "Slider"}
          >
            {formatValue && (
              <span className={sliderStyles.sliderTooltip}>
                {formatValue(value)}
              </span>
            )}
          </BaseSlider.Thumb>
        </BaseSlider.Root>
      </div>
    </div>
  );
}
